import * as XLSX from 'xlsx';
import { CUKCUK_DEFAULT_CITY, CUKCUK_PARSER_VERSION } from '../constants/cukcuk';
import {
  getSheetHiddenColumns,
  getSheetMatrix,
  readCukcukWorkbookData,
} from './cukcukXlsxWorkerClient';
import {
  normalizeSheetHint,
  pickSheetName,
  readFileAsArrayBuffer,
  readSheetMatrix,
  readWorkbookFromArrayBuffer,
} from './xlsxUtils';
import type { Dish } from '../types/dish';
import type { InvoiceSelectedOption } from '../types/invoiceNotification';
import {
  CUKCUK_INVOICE_COLUMNS as INV,
  CUKCUK_MENU_COLUMNS as COL,
  type CukcukDishBlock,
  type CukcukDishSource,
  type CukcukIngredientRef,
  type CukcukInvoice,
  type CukcukInvoiceLineItem,
  type CukcukInvoiceParseMeta,
  type CukcukInvoiceParseResult,
  type CukcukParseMeta,
  type CukcukParseResult,
  type CukcukStpvRow,
  type DishOption,
  type MenuOption,
  type MenuOptionRef,
  type ParsedCukcukDish,
} from '../types/cukcukImport';

const PARSER_VERSION = CUKCUK_PARSER_VERSION;

const MENU_SHEET_HINTS = [
  'thucdon',
  'danhmucthucdon',
  'menu',
  'monan',
];

const EXTENSION_COLUMN_START = COL.NVL_CODE;
const STPV_COLUMN_NAME = 'Sở thích phục vụ';
const EXTRA_PRICE_COLUMN_NAME = 'Thu thêm';

// ─── Normalization helpers ───────────────────────────────────────────────────

const normalizeHeader = (value: unknown): string =>
  normalizeSheetHint(String(value ?? '')).replace(/[^a-z0-9]/g, '');

const normalizeCode = (value: unknown): string => String(value ?? '').trim();

const isBlank = (value: unknown): boolean => String(value ?? '').trim() === '';

const toNumber = (value: unknown): number => {
  if (value === undefined || value === null || value === '') return 0;
  const normalized = String(value).replace(/[,\s]/g, '').replace(/[^\d.-]/g, '');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const slugifyOptionCode = (label: string): string =>
  label
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/gi, 'd')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 56);

export interface InferredStpvGroup {
  groupName: string;
  optionLabel: string;
  isRequired: boolean;
  limit: number;
}

/**
 * Suy luận nhóm STPV từ nhãn CUKCUK (vd: "Đổi hạt: ETHIOPIA...", "200g", "Nóng").
 */
export const inferCukcukStpvGroup = (label: string): InferredStpvGroup => {
  const trimmed = label.trim();

  if (/^\d+(g|kg)$/i.test(trimmed)) {
    return { groupName: 'Trọng lượng', optionLabel: trimmed, isRequired: true, limit: 1 };
  }

  if (/^đổi hạt:/i.test(trimmed)) {
    return { groupName: 'Đổi hạt', optionLabel: trimmed, isRequired: false, limit: 1 };
  }

  if (/^thêm shot:/i.test(trimmed)) {
    return { groupName: 'Thêm shot', optionLabel: trimmed, isRequired: false, limit: 1 };
  }

  if (trimmed === 'Nóng' || trimmed === 'Đá') {
    return { groupName: 'Nhiệt độ', optionLabel: trimmed, isRequired: true, limit: 1 };
  }

  if (trimmed === 'Mang đi') {
    return { groupName: 'Phục vụ', optionLabel: trimmed, isRequired: false, limit: 1 };
  }

  if (/to (arabica|robusta)/i.test(trimmed) || /coldbrew to espresso/i.test(trimmed)) {
    return { groupName: 'Đổi loại hạt', optionLabel: trimmed, isRequired: false, limit: 1 };
  }

  if (/^ly /i.test(trimmed)) {
    return { groupName: 'Loại ly', optionLabel: trimmed, isRequired: false, limit: 1 };
  }

  return { groupName: 'Sở thích phục vụ', optionLabel: trimmed, isRequired: false, limit: 1 };
};

export const buildCukcukOptionCode = (label: string, dishCode?: string): string => {
  const slug = slugifyOptionCode(label);

  if (dishCode && /^\d+(G|KG)$/.test(slug)) {
    return `${dishCode}-${slug}`;
  }

  return `STPV-${slug}`;
};

/** Giữ tương thích API cũ — parse chuỗi STPV dạng nhóm:mã1,mã2 */
export const parseStpvBindingValue = (rawValue: unknown) => {
  if (isBlank(rawValue)) return [];

  const text = String(rawValue).trim();
  const segments = text.includes('|') ? text.split('|') : [text];
  const bindings: Array<{ groupName: string; optionCodes: string[] }> = [];

  segments.forEach((segment) => {
    const trimmed = segment.trim();
    if (!trimmed) return;

    const pairMatch = trimmed.match(/^([^:=]+)[:=](.+)$/);
    if (pairMatch) {
      bindings.push({
        groupName: pairMatch[1].trim(),
        optionCodes: trimmed.includes(';')
          ? pairMatch[2].split(';').map((part) => part.trim()).filter(Boolean)
          : pairMatch[2].split(',').map((part) => part.trim()).filter(Boolean),
      });
      return;
    }

    if (trimmed.includes(';')) {
      trimmed.split(';').map((part) => part.trim()).filter(Boolean).forEach((groupName) => {
        bindings.push({ groupName, optionCodes: [] });
      });
      return;
    }

    bindings.push({ groupName: trimmed, optionCodes: [] });
  });

  return bindings;
};

// ─── Worksheet helpers ───────────────────────────────────────────────────────

const getHiddenColumnIndexes = (worksheet: XLSX.WorkSheet): Set<number> => {
  const hidden = new Set<number>();
  (worksheet['!cols'] ?? []).forEach((col, index) => {
    if (col?.hidden) hidden.add(index);
  });
  return hidden;
};

const rowToRecord = (row: unknown[], headers: string[]): Record<string, unknown> => {
  const record: Record<string, unknown> = {};
  headers.forEach((header, index) => {
    if (!header) return;
    record[header] = row[index] ?? '';
  });
  return record;
};

const isExtensionColumn = (columnIndex: number, hiddenColumns: Set<number>): boolean =>
  columnIndex >= EXTENSION_COLUMN_START || hiddenColumns.has(columnIndex);

const findCukcukMenuHeaderRow = (rows: unknown[][]): number => {
  for (let index = 0; index < Math.min(rows.length, 30); index += 1) {
    const row = rows[index] ?? [];
    const col0 = normalizeHeader(row[COL.TYPE]);
    const col1 = normalizeHeader(row[COL.CODE]);
    const col22 = normalizeHeader(row[COL.STPV_LABEL]);

    if (col0.includes('loai') && col1.includes('mamon') && col22.includes('sothichphucvu')) {
      return index;
    }
  }

  throw new Error('Không tìm thấy dòng tiêu đề cột trên sheet Thực đơn CUKCUK.');
};

/** Nhận diện ma trận sheet Thực đơn CUKCUK qua dòng tiêu đề. */
export const isCukcukMenuMatrix = (rows: unknown[][]): boolean => {
  for (let index = 0; index < Math.min(rows.length, 30); index += 1) {
    const row = rows[index] ?? [];
    const col0 = normalizeHeader(row[COL.TYPE]);
    const col1 = normalizeHeader(row[COL.CODE]);
    const col22 = normalizeHeader(row[COL.STPV_LABEL]);

    if (col0.includes('loai') && col1.includes('mamon') && col22.includes('sothichphucvu')) {
      return true;
    }
  }

  return false;
};

/** Kiểm tra file Excel có phải export DANHMUCTHUCDON CUKCUK hay không. */
export const isCukcukMenuFile = async (file: File): Promise<boolean> => {
  try {
    const buffer = await readFileAsArrayBuffer(file);
    const workbook = readWorkbookFromArrayBuffer(buffer);
    const sheetNames = workbook.SheetNames ?? [];
    const menuSheetName = pickSheetName(sheetNames, MENU_SHEET_HINTS, 0);
    if (!menuSheetName) return false;

    const worksheet = workbook.Sheets[menuSheetName];
    if (!worksheet) return false;

    return isCukcukMenuMatrix(readSheetMatrix(worksheet));
  } catch {
    return false;
  }
};

const isSubHeaderRow = (row: unknown[]): boolean => {
  const col7 = String(row[COL.SPECIAL_FEE_AMOUNT] ?? '').trim();
  const col8 = String(row[7] ?? '').trim();
  return col7 === 'Số tiền' || col8 === 'Phần trăm (%)';
};

const hasContinuationData = (row: unknown[]): boolean =>
  !isBlank(row[COL.NVL_CODE]) ||
  !isBlank(row[COL.STPV_LABEL]) ||
  !isBlank(row[COL.SUB_DISH_CODE]) ||
  !isBlank(row[COL.COMBO_CODE]);

const isSkippableRow = (row: unknown[]): boolean => {
  if (hasContinuationData(row)) return false;

  const code = normalizeCode(row[COL.CODE]);
  const type = String(row[COL.TYPE] ?? '').trim();

  if (!code && !type) return true;
  if (code === 'Mã món (*)') return true;
  if (type.startsWith('Thông tin món')) return true;
  if (type.startsWith('Xem các ví dụ')) return true;

  return false;
};

const parseIngredientRow = (row: unknown[]): CukcukIngredientRef | null => {
  const code = normalizeCode(row[COL.NVL_CODE]);
  if (!code) return null;

  const rawQty = row[COL.NVL_QTY];
  const quantity =
    rawQty === undefined || rawQty === ''
      ? undefined
      : typeof rawQty === 'number'
        ? rawQty
        : String(rawQty);

  return {
    code,
    name: String(row[COL.NVL_NAME] ?? '').trim() || undefined,
    unit: String(row[COL.NVL_UNIT] ?? '').trim() || undefined,
    quantity,
  };
};

const parseStpvRow = (
  row: unknown[],
  rowNumber: number,
  hiddenColumns: Set<number>,
): CukcukStpvRow | null => {
  const label = String(row[COL.STPV_LABEL] ?? '').trim();
  if (!label || label === STPV_COLUMN_NAME) return null;

  return {
    label,
    extraPrice: toNumber(row[COL.STPV_EXTRA_PRICE]),
    rowNumber,
    isHiddenColumn: isExtensionColumn(COL.STPV_LABEL, hiddenColumns),
  };
};

const mapCukcukVisibility = (value: unknown): boolean => {
  const text = String(value ?? '').trim().toLowerCase();
  if (!text) return true;
  return text !== 'không' && text !== 'khong';
};

const defaultDishPriceConfiguration = (): Dish['priceConfiguration'] => ({
  allowPriceEdit: false,
  requireQuantity: false,
  allowRemoveWithoutPermission: false,
});

const resolveOptionPrice = (dishBasePrice: number, extraPrice: number, groupName: string): number => {
  if (groupName === 'Trọng lượng' && dishBasePrice === 0) {
    return extraPrice;
  }
  return extraPrice;
};

// ─── Block parser ────────────────────────────────────────────────────────────

export const parseCukcukDishBlocksFromMatrix = (
  rows: unknown[][],
  sheetName: string,
  hiddenColumns: Set<number> = new Set(),
): { blocks: CukcukDishBlock[]; headerRowIndex: number; dataStartRowIndex: number; headers: string[] } => {
  const headerRowIndex = findCukcukMenuHeaderRow(rows);
  const headers = (rows[headerRowIndex] ?? []).map((cell) => String(cell ?? '').trim());

  let dataStartRowIndex = headerRowIndex + 1;
  if (isSubHeaderRow(rows[dataStartRowIndex] ?? [])) {
    dataStartRowIndex += 1;
  }

  const blocks: CukcukDishBlock[] = [];
  let current: CukcukDishBlock | null = null;

  for (let rowIndex = dataStartRowIndex; rowIndex < rows.length; rowIndex += 1) {
    const row = rows[rowIndex] ?? [];
    if (isSkippableRow(row)) continue;

    const code = normalizeCode(row[COL.CODE]);

    if (code) {
      if (current) blocks.push(current);

      current = {
        code,
        name: String(row[COL.NAME] ?? '').trim(),
        cukcukType: String(row[COL.TYPE] ?? '').trim(),
        unit: String(row[COL.UNIT] ?? '').trim() || 'Phần',
        price: toNumber(row[COL.PRICE]),
        barcode: String(row[COL.BARCODE] ?? '').trim() || undefined,
        description: String(row[COL.DESCRIPTION] ?? '').trim() || undefined,
        menuGroup: String(row[COL.MENU_GROUP] ?? '').trim() || undefined,
        displayOrder: String(row[COL.DISPLAY_ORDER] ?? '').trim() || undefined,
        isVisible: mapCukcukVisibility(row[COL.IS_VISIBLE]),
        startRowNumber: rowIndex + 1,
        ingredients: [],
        stpvRows: [],
        subDishCodes: [],
        comboCodes: [],
        rawMainRow: rowToRecord(row, headers),
      };

      const ingredient = parseIngredientRow(row);
      if (ingredient) current.ingredients.push(ingredient);

      const stpv = parseStpvRow(row, rowIndex + 1, hiddenColumns);
      if (stpv) current.stpvRows.push(stpv);

      const subCode = normalizeCode(row[COL.SUB_DISH_CODE]);
      if (subCode) current.subDishCodes.push(subCode);

      const comboCode = normalizeCode(row[COL.COMBO_CODE]);
      if (comboCode) current.comboCodes.push(comboCode);

      continue;
    }

    if (!current || !hasContinuationData(row)) continue;

    const ingredient = parseIngredientRow(row);
    if (ingredient) current.ingredients.push(ingredient);

    const stpv = parseStpvRow(row, rowIndex + 1, hiddenColumns);
    if (stpv) current.stpvRows.push(stpv);

    const subCode = normalizeCode(row[COL.SUB_DISH_CODE]);
    if (subCode) current.subDishCodes.push(subCode);

    const comboCode = normalizeCode(row[COL.COMBO_CODE]);
    if (comboCode) current.comboCodes.push(comboCode);
  }

  if (current) blocks.push(current);

  return { blocks, headerRowIndex, dataStartRowIndex, headers };
};

export const parseCukcukDishBlocks = (
  worksheet: XLSX.WorkSheet,
  sheetName: string,
): { blocks: CukcukDishBlock[]; headerRowIndex: number; dataStartRowIndex: number; headers: string[] } =>
  parseCukcukDishBlocksFromMatrix(readSheetMatrix(worksheet), sheetName, getHiddenColumnIndexes(worksheet));

const blockToDish = (block: CukcukDishBlock, sheetName: string, defaultCity: string): ParsedCukcukDish => {
  const dish: Partial<Dish> = {
    code: block.code,
    name: block.name,
    price: block.price,
    unit: block.unit,
    city: defaultCity,
    vat: 0,
    preparationTime: 0,
    isActive: block.isVisible,
    priceConfiguration: defaultDishPriceConfiguration(),
  };

  if (block.barcode) dish.barcode = block.barcode;
  if (block.description) dish.description = block.description;
  if (block.menuGroup) {
    dish.group = block.menuGroup;
    dish.groupName = block.menuGroup;
  }
  if (block.cukcukType) {
    dish.type = block.cukcukType;
    dish.typeName = block.cukcukType;
  }
  if (block.displayOrder) {
    const order = toNumber(block.displayOrder);
    if (order > 0) dish.displayOrder = order;
  }

  const source: CukcukDishSource = {
    sheetName,
    rowNumber: block.startRowNumber,
    rawRow: block.rawMainRow,
    cukcukType: block.cukcukType,
    ingredients: block.ingredients.length ? block.ingredients : undefined,
  };

  return { dish, source };
};

const buildMenuOption = (
  label: string,
  extraPrice: number,
  dishCode: string,
  dishBasePrice: number,
  rowNumber: number,
  isHiddenColumn: boolean,
  sheetName: string,
): MenuOption => {
  const inferred = inferCukcukStpvGroup(label);
  const code = buildCukcukOptionCode(label, dishCode);

  return {
    code,
    name: inferred.optionLabel,
    price: resolveOptionPrice(dishBasePrice, extraPrice, inferred.groupName),
    isActive: true,
    optionGroupName: inferred.groupName,
    source: {
      cukcukCode: code,
      cukcukName: inferred.optionLabel,
      cukcukGroupName: inferred.groupName,
      sheetName,
      rowNumber,
      columnName: STPV_COLUMN_NAME,
      isHiddenColumn,
    },
  };
};

const buildDishOptionsFromBlock = (block: CukcukDishBlock, sheetName: string): DishOption[] => {
  const grouped = new Map<string, {
    isRequired: boolean;
    limit: number;
    options: MenuOptionRef[];
    rowNumbers: number[];
  }>();

  block.stpvRows.forEach((stpv, index) => {
    const inferred = inferCukcukStpvGroup(stpv.label);
    const optionCode = buildCukcukOptionCode(stpv.label, block.code);
    const bucket = grouped.get(inferred.groupName) ?? {
      isRequired: inferred.isRequired,
      limit: inferred.limit,
      options: [],
      rowNumbers: [],
    };

    bucket.options.push({
      code: optionCode,
      name: inferred.optionLabel,
      price: resolveOptionPrice(block.price, stpv.extraPrice, inferred.groupName),
      isActive: true,
      displayOrder: index + 1,
    });
    bucket.rowNumbers.push(stpv.rowNumber);
    grouped.set(inferred.groupName, bucket);
  });

  return Array.from(grouped.entries()).map(([groupName, value], index) => ({
    dishCode: block.code,
    dishName: block.name,
    groupName,
    isRequired: value.isRequired,
    limit: value.limit,
    options: value.options,
    displayOrder: index + 1,
    source: {
      cukcukDishCode: block.code,
      cukcukDishName: block.name,
      cukcukGroupName: groupName,
      cukcukOptionCodes: value.options.map((opt) => opt.code),
      sheetName,
      rowNumber: value.rowNumbers[0],
      columnName: STPV_COLUMN_NAME,
      isHiddenColumn: block.stpvRows.some((row) => row.isHiddenColumn),
      rawRow: block.rawMainRow,
    },
  }));
};

// ─── Public extract API ──────────────────────────────────────────────────────

export interface ExtractedMenuStpv {
  dishCode: string;
  dishName?: string;
  bindings: Array<{
    groupName: string;
    optionCodes: string[];
    isRequired?: boolean;
    limit?: number;
  }>;
  sourceColumn: string;
  isHiddenColumn: boolean;
  rawValue: unknown;
  rowNumber: number;
  rawRow: Record<string, unknown>;
}

/**
 * Trích xuất Sở thích phục vụ từ sheet "Thực đơn" CUKCUK,
 * gồm các dòng nối tiếp và cột mở rộng (NVL / STPV).
 */
export const extractServicePreferencesFromMenuSheet = (
  worksheet: XLSX.WorkSheet,
  sheetName: string,
): ExtractedMenuStpv[] => {
  const { blocks } = parseCukcukDishBlocks(worksheet, sheetName);

  return blocks
    .filter((block) => block.stpvRows.length > 0)
    .map((block) => {
      const dishOptions = buildDishOptionsFromBlock(block, sheetName);

      return {
        dishCode: block.code,
        dishName: block.name,
        bindings: dishOptions.map((item) => ({
          groupName: item.groupName,
          optionCodes: item.options.map((opt) => opt.code),
          isRequired: item.isRequired,
          limit: item.limit,
        })),
        sourceColumn: STPV_COLUMN_NAME,
        isHiddenColumn: block.stpvRows.some((row) => row.isHiddenColumn),
        rawValue: block.stpvRows.map((row) => row.label).join(' | '),
        rowNumber: block.startRowNumber,
        rawRow: block.rawMainRow,
      };
    });
};

// ─── Workbook / file helpers ─────────────────────────────────────────────────

const dedupeOptions = (options: MenuOption[]): MenuOption[] => {
  const map = new Map<string, MenuOption>();
  options.forEach((option) => map.set(option.code, option));
  return Array.from(map.values());
};

// ─── Main entry ──────────────────────────────────────────────────────────────

export interface ParseCukcukMenuOptions {
  defaultCity?: string;
}

/**
 * Parse file DANHMUCTHUCDON (.xls/.xlsx) từ CUKCUK POS.
 * Sheet chính: "Thực đơn" — đọc món, STPV (cột W/X) và dòng nối tiếp.
 */
export const parseCukcukMenu = async (
  file: File,
  parseOptions: ParseCukcukMenuOptions = {},
): Promise<CukcukParseResult> => {
  const defaultCity = parseOptions.defaultCity ?? CUKCUK_DEFAULT_CITY;
  const warnings: string[] = [];

  const workbookData = await readCukcukWorkbookData(file);
  const sheetNames = workbookData.sheetNames;

  if (!sheetNames.length) {
    throw new Error('File Excel không có sheet nào.');
  }

  const menuSheetName = pickSheetName(sheetNames, MENU_SHEET_HINTS, 0);
  if (!menuSheetName) {
    throw new Error('Không tìm thấy sheet Thực đơn trong file CUKCUK.');
  }

  const menuSheet = workbookData.sheets[menuSheetName];
  if (!menuSheet) {
    throw new Error(`Không đọc được sheet menu: ${menuSheetName}`);
  }

  const { blocks, headerRowIndex, dataStartRowIndex } = parseCukcukDishBlocksFromMatrix(
    getSheetMatrix(menuSheet),
    menuSheetName,
    getSheetHiddenColumns(menuSheet),
  );

  if (!blocks.length) {
    warnings.push('Không parse được món nào — kiểm tra lại định dạng file CUKCUK.');
  }

  const dishes = blocks.map((block) => blockToDish(block, menuSheetName, defaultCity));

  const options = dedupeOptions(
    blocks.flatMap((block) =>
      block.stpvRows.map((stpv) =>
        buildMenuOption(
          stpv.label,
          stpv.extraPrice,
          block.code,
          block.price,
          stpv.rowNumber,
          stpv.isHiddenColumn,
          menuSheetName,
        ),
      ),
    ),
  );

  const dishOptions = blocks.flatMap((block) => buildDishOptionsFromBlock(block, menuSheetName));

  const hiddenColumnsUsed = Array.from(
    new Set(
      blocks
        .flatMap((block) => block.stpvRows)
        .filter((row) => row.isHiddenColumn)
        .map(() => STPV_COLUMN_NAME),
    ),
  );

  if (!options.length) {
    warnings.push('Không tìm thấy Sở thích phục vụ trên sheet Thực đơn.');
  }

  const meta: CukcukParseMeta = {
    fileName: file.name,
    sheetNames,
    menuSheetName,
    parserVersion: PARSER_VERSION,
    warnings,
    hiddenColumnsUsed,
    headerRowIndex: headerRowIndex + 1,
    dataStartRowIndex: dataStartRowIndex + 1,
  };

  return { dishes, options, dishOptions, meta };
};

// ─── Invoice parser (Chi tiết doanh thu theo hóa đơn) ───────────────────────

const INVOICE_SHEET_HINTS = [
  'chitietdoanhthu',
  'doanhthu',
  'hoadon',
  'sheet1',
];

const parseExcelDate = (value: unknown): string | undefined => {
  if (value === undefined || value === null || value === '') return undefined;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const day = String(value.getDate()).padStart(2, '0');
    const month = String(value.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}/${value.getFullYear()}`;
  }

  if (typeof value === 'number') {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (!parsed) return String(value);
    const day = String(parsed.d).padStart(2, '0');
    const month = String(parsed.m).padStart(2, '0');
    return `${day}/${month}/${parsed.y}`;
  }

  return String(value).trim() || undefined;
};

const parseTimeRange = (value: unknown): { checkIn?: string; checkOut?: string } => {
  const text = String(value ?? '').trim();
  if (!text) return {};

  const parts = text.split('-').map((part) => part.trim()).filter(Boolean);
  if (!parts.length) return {};

  return {
    checkIn: parts[0],
    checkOut: parts[1] ?? parts[0],
  };
};

const findCukcukInvoiceHeaderRow = (rows: unknown[][]): number => {
  for (let index = 0; index < Math.min(rows.length, 30); index += 1) {
    const row = rows[index] ?? [];
    const invoiceHeader = normalizeHeader(row[INV.INVOICE_CODE]);
    const itemCodeHeader = normalizeHeader(row[INV.ITEM_CODE]);
    const itemNameHeader = normalizeHeader(row[INV.ITEM_NAME]);

    if (
      invoiceHeader.includes('sohoadon') &&
      itemCodeHeader.includes('mamon') &&
      itemNameHeader.includes('tenmon')
    ) {
      return index;
    }
  }

  throw new Error('Không tìm thấy dòng tiêu đề trên file chi tiết doanh thu CUKCUK.');
};

const isInvoiceSubHeaderRow = (row: unknown[]): boolean =>
  String(row[INV.LINE_TOTAL] ?? '').trim() === 'Tổng';

const isInvoiceSummaryRow = (row: unknown[]): boolean => {
  const invoiceCode = normalizeCode(row[INV.INVOICE_CODE]);
  const itemCode = normalizeCode(row[INV.ITEM_CODE]);
  const itemName = String(row[INV.ITEM_NAME] ?? '').trim();
  return !!invoiceCode && !itemCode && !itemName;
};

const isMainItemRow = (row: unknown[]): boolean => !!normalizeCode(row[INV.ITEM_CODE]);

const isOptionItemRow = (row: unknown[]): boolean => {
  const itemCode = normalizeCode(row[INV.ITEM_CODE]);
  const itemName = String(row[INV.ITEM_NAME] ?? '').trim();
  return !itemCode && !!itemName;
};

export interface InvoiceRowGroup {
  invoiceCode: string;
  rows: unknown[][];
  summaryRow?: unknown[];
}

/** Nhận diện ma trận sheet chi tiết doanh thu CUKCUK qua dòng tiêu đề. */
export const isCukcukInvoiceMatrix = (rows: unknown[][]): boolean => {
  for (let index = 0; index < Math.min(rows.length, 30); index += 1) {
    const row = rows[index] ?? [];
    const invoiceHeader = normalizeHeader(row[INV.INVOICE_CODE]);
    const itemCodeHeader = normalizeHeader(row[INV.ITEM_CODE]);
    const itemNameHeader = normalizeHeader(row[INV.ITEM_NAME]);

    if (
      invoiceHeader.includes('sohoadon') &&
      itemCodeHeader.includes('mamon') &&
      itemNameHeader.includes('tenmon')
    ) {
      return true;
    }
  }

  return false;
};

export const resolveCukcukInvoiceDataBounds = (
  rows: unknown[][],
): { headerRowIndex: number; dataStartRowIndex: number } => {
  const headerRowIndex = findCukcukInvoiceHeaderRow(rows);
  let dataStartRowIndex = headerRowIndex + 1;

  if (isInvoiceSubHeaderRow(rows[dataStartRowIndex] ?? [])) {
    dataStartRowIndex += 1;
  }

  return { headerRowIndex, dataStartRowIndex };
};

/**
 * Gom các dòng dữ liệu (món chính + tùy chọn) theo mã hóa đơn, giữ thứ tự xuất hiện.
 */
export const groupInvoiceRowsByCode = (
  rows: unknown[][],
  dataStartRowIndex: number,
): InvoiceRowGroup[] => {
  const groupMap = new Map<string, InvoiceRowGroup>();
  const order: string[] = [];

  for (let rowIndex = dataStartRowIndex; rowIndex < rows.length; rowIndex += 1) {
    const row = rows[rowIndex] ?? [];
    const invoiceCode = normalizeCode(row[INV.INVOICE_CODE]);
    if (!invoiceCode) continue;

    let group = groupMap.get(invoiceCode);
    if (!group) {
      group = { invoiceCode, rows: [] };
      groupMap.set(invoiceCode, group);
      order.push(invoiceCode);
    }

    if (isInvoiceSummaryRow(row)) {
      group.summaryRow = row;
      continue;
    }

    if (isMainItemRow(row) || isOptionItemRow(row)) {
      group.rows.push(row);
    }
  }

  return order.map((code) => groupMap.get(code)!);
};

/** Kiểm tra file Excel có phải export chi tiết doanh thu CUKCUK hay không. */
export const isCukcukInvoiceFile = async (file: File): Promise<boolean> => {
  try {
    const buffer = await readFileAsArrayBuffer(file);
    const workbook = readWorkbookFromArrayBuffer(buffer);
    const sheetNames = workbook.SheetNames ?? [];
    const sheetName = pickSheetName(sheetNames, INVOICE_SHEET_HINTS, 0);
    if (!sheetName) return false;

    const worksheet = workbook.Sheets[sheetName];
    if (!worksheet) return false;

    return isCukcukInvoiceMatrix(readSheetMatrix(worksheet));
  } catch {
    return false;
  }
};

const applyInvoiceSummary = (invoice: CukcukInvoice, row: unknown[]): void => {
  const times = parseTimeRange(row[INV.TIME_RANGE]);

  invoice.saleDate = parseExcelDate(row[INV.SALE_DATE]);
  invoice.checkInTime = times.checkIn;
  invoice.checkOutTime = times.checkOut;
  invoice.totalAmount = toNumber(row[INV.LINE_TOTAL]) || undefined;
  invoice.merchandiseAmount = toNumber(row[INV.MERCHANDISE_AMOUNT]) || undefined;
  invoice.feeAmount = toNumber(row[INV.FEE_AMOUNT]) || undefined;
  invoice.vatAmount = toNumber(row[INV.VAT_AMOUNT]) || undefined;
  invoice.discountAmount = toNumber(row[INV.DISCOUNT_AMOUNT]) || undefined;

  const guestCount = toNumber(row[INV.GUEST_COUNT]);
  if (guestCount > 0) invoice.guestCount = guestCount;

  const customerName = String(row[INV.CUSTOMER_NAME] ?? '').trim();
  if (customerName) invoice.customerName = customerName;

  const serviceMode = String(row[INV.SERVICE_MODE] ?? '').trim();
  if (serviceMode) invoice.serviceMode = serviceMode;

  const area = String(row[INV.AREA] ?? '').trim();
  if (area) invoice.area = area;

  const tableName = String(row[INV.TABLE_NAME] ?? '').trim();
  if (tableName) invoice.tableName = tableName;

  const shiftName = String(row[INV.SHIFT_NAME] ?? '').trim();
  if (shiftName) invoice.shiftName = shiftName;

  const cashierName = String(row[INV.CASHIER_NAME] ?? '').trim();
  if (cashierName) invoice.cashierName = cashierName;

  const serverName = String(row[INV.SERVER_NAME] ?? '').trim();
  if (serverName) invoice.serverName = serverName;
};

const parseMainInvoiceLineItem = (row: unknown[], rowNumber: number): CukcukInvoiceLineItem => {
  const itemId = normalizeCode(row[INV.ITEM_CODE]);
  const itemName = String(row[INV.ITEM_NAME] ?? '').trim();
  const unit = String(row[INV.UNIT] ?? '').trim();
  const itemGroup = String(row[INV.MENU_GROUP] ?? '').trim();
  const itemType = String(row[INV.ITEM_TYPE] ?? '').trim();
  const vatRate = String(row[INV.VAT_RATE] ?? '').trim();
  const quantityValue = row[INV.QUANTITY];

  return {
    itemId,
    itemName,
    quantity: isBlank(quantityValue) ? 1 : toNumber(quantityValue),
    unit: unit || undefined,
    unitPrice: toNumber(row[INV.UNIT_PRICE]),
    lineTotal: toNumber(row[INV.LINE_TOTAL]),
    itemGroup: itemGroup || undefined,
    itemType: itemType || undefined,
    vatRate: vatRate || undefined,
    selectedOptions: [],
    sourceRowNumber: rowNumber,
  };
};

const parseInvoiceSelectedOption = (row: unknown[], parentItemId?: string): InvoiceSelectedOption => {
  const optionName = String(row[INV.ITEM_NAME] ?? '').trim();
  const quantity = toNumber(row[INV.QUANTITY]);
  const unitPrice = toNumber(row[INV.UNIT_PRICE]);
  const lineTotal = toNumber(row[INV.LINE_TOTAL]);
  const unit = String(row[INV.UNIT] ?? '').trim();

  return {
    optionId: buildCukcukOptionCode(optionName, parentItemId),
    optionName,
    quantity: quantity > 0 ? quantity : undefined,
    unit: unit || undefined,
    price: unitPrice > 0 ? unitPrice : undefined,
    amount: lineTotal > 0 ? lineTotal : unitPrice > 0 ? unitPrice : undefined,
  };
};

const getOrCreateInvoice = (
  invoiceMap: Map<string, CukcukInvoice>,
  invoiceOrder: string[],
  invoiceCode: string,
): CukcukInvoice => {
  const existing = invoiceMap.get(invoiceCode);
  if (existing) return existing;

  const created: CukcukInvoice = {
    invoiceCode,
    items: [],
  };
  invoiceMap.set(invoiceCode, created);
  invoiceOrder.push(invoiceCode);
  return created;
};

export interface ParseCukcukInvoiceRowsResult {
  invoices: CukcukInvoice[];
  headerRowIndex: number;
  dataStartRowIndex: number;
  warnings: string[];
  totalLineItems: number;
  totalOptions: number;
}

/**
 * Parse sheet chi tiết doanh thu CUKCUK thành danh sách hóa đơn.
 * Dòng không có Mã món được gắn làm selectedOptions của món chính gần nhất.
 */
export const parseCukcukInvoiceRows = (
  worksheet: XLSX.WorkSheet,
  sheetName: string,
): ParseCukcukInvoiceRowsResult => {
  const rows = readSheetMatrix(worksheet);
  const headerRowIndex = findCukcukInvoiceHeaderRow(rows);

  let dataStartRowIndex = headerRowIndex + 1;
  if (isInvoiceSubHeaderRow(rows[dataStartRowIndex] ?? [])) {
    dataStartRowIndex += 1;
  }

  const warnings: string[] = [];
  const invoiceMap = new Map<string, CukcukInvoice>();
  const invoiceOrder: string[] = [];
  let totalLineItems = 0;
  let totalOptions = 0;

  for (let rowIndex = dataStartRowIndex; rowIndex < rows.length; rowIndex += 1) {
    const row = rows[rowIndex] ?? [];
    const invoiceCode = normalizeCode(row[INV.INVOICE_CODE]);
    if (!invoiceCode) continue;

    if (isInvoiceSummaryRow(row)) {
      const invoice = getOrCreateInvoice(invoiceMap, invoiceOrder, invoiceCode);
      applyInvoiceSummary(invoice, row);
      continue;
    }

    if (isMainItemRow(row)) {
      const invoice = getOrCreateInvoice(invoiceMap, invoiceOrder, invoiceCode);
      invoice.items.push(parseMainInvoiceLineItem(row, rowIndex + 1));
      totalLineItems += 1;
      continue;
    }

    if (isOptionItemRow(row)) {
      const invoice = getOrCreateInvoice(invoiceMap, invoiceOrder, invoiceCode);
      const parentItem = invoice.items[invoice.items.length - 1];

      if (!parentItem) {
        warnings.push(
          `Dòng ${rowIndex + 1}: tùy chọn "${String(row[INV.ITEM_NAME] ?? '').trim()}" không có món chính trên hóa đơn ${invoiceCode}.`,
        );
        continue;
      }

      parentItem.selectedOptions.push(parseInvoiceSelectedOption(row, parentItem.itemId));
      totalOptions += 1;
      continue;
    }
  }

  return {
    invoices: invoiceOrder.map((code) => invoiceMap.get(code)!),
    headerRowIndex,
    dataStartRowIndex,
    warnings,
    totalLineItems,
    totalOptions,
  };
};

export const parseCukcukInvoiceRowsFromMatrix = (
  rows: unknown[][],
  sheetName: string,
): ParseCukcukInvoiceRowsResult => {
  const worksheet = XLSX.utils.aoa_to_sheet(rows);
  return parseCukcukInvoiceRows(worksheet, sheetName);
};

/**
 * Parse file chi tiết doanh thu theo hóa đơn và mặt hàng từ CUKCUK POS.
 */
export const parseCukcukInvoices = async (file: File): Promise<CukcukInvoiceParseResult> => {
  const workbookData = await readCukcukWorkbookData(file);
  const sheetNames = workbookData.sheetNames;

  if (!sheetNames.length) {
    throw new Error('File Excel không có sheet nào.');
  }

  const sheetName = pickSheetName(sheetNames, INVOICE_SHEET_HINTS, 0);
  if (!sheetName) {
    throw new Error('Không tìm thấy sheet dữ liệu hóa đơn trong file CUKCUK.');
  }

  const sheet = workbookData.sheets[sheetName];
  if (!sheet) {
    throw new Error(`Không đọc được sheet: ${sheetName}`);
  }

  const parsed = parseCukcukInvoiceRowsFromMatrix(getSheetMatrix(sheet), sheetName);

  const meta: CukcukInvoiceParseMeta = {
    fileName: file.name,
    sheetNames,
    sheetName,
    parserVersion: PARSER_VERSION,
    warnings: parsed.warnings,
    headerRowIndex: parsed.headerRowIndex + 1,
    dataStartRowIndex: parsed.dataStartRowIndex + 1,
    totalInvoices: parsed.invoices.length,
    totalLineItems: parsed.totalLineItems,
    totalOptions: parsed.totalOptions,
  };

  return {
    invoices: parsed.invoices,
    meta,
  };
};
